import type {
  EnvironmentId,
  OrchestrationReadModel,
  OrchestrationShellSnapshot,
  ProviderKind,
} from "@cmmd-forge/contracts";
import { resolveModelSlugForProvider } from "@cmmd-forge/shared/model";

import type { Project } from "./types";

/**
 * A project as the store holds it, from either snapshot the server sends.
 *
 * Moved out of `store.ts` with the rest of the project mapping, so the one
 * place that turns a server project into a `Project` sits next to the one
 * place that patches it.
 */
export function mapProject(
  project:
    | OrchestrationReadModel["projects"][number]
    | OrchestrationShellSnapshot["projects"][number],
  environmentId: EnvironmentId,
): Project {
  return {
    id: project.id,
    environmentId,
    name: project.title,
    cwd: project.workspaceRoot,
    repositoryIdentity: project.repositoryIdentity ?? null,
    availability: project.availability,
    defaultThreadEnvMode: project.defaultThreadEnvMode ?? null,
    // The Registration a Checkout was created from. Null for every project
    // that was not, which the cloud row resolver then matches by directory.
    cmmdRegistration: project.cmmdRegistration ?? null,
    defaultModelSelection: project.defaultModelSelection
      ? normalizeModelSelection(project.defaultModelSelection)
      : null,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    scripts: mapProjectScripts(project.scripts),
  };
}

/**
 * A stored model pair with its model resolved to the provider's real slug.
 *
 * Aliases reach the store from persisted settings and from other machines, and
 * a pair whose model does not resolve for its provider is the shape of every
 * mispairing this codebase has shipped.
 */
export function normalizeModelSelection<T extends { provider: ProviderKind; model: string }>(
  selection: T,
): T {
  return {
    ...selection,
    model: resolveModelSlugForProvider(selection.provider, selection.model),
  };
}

/** Copied, not aliased: the store's arrays must not share identity with a payload. */
function mapProjectScripts(scripts: ReadonlyArray<Project["scripts"][number]>): Project["scripts"] {
  return scripts.map((script) => ({ ...script }));
}

/** The fields a `project.meta-updated` event may carry. */
export interface ProjectMetaUpdatePayload {
  readonly title?: string | undefined;
  readonly workspaceRoot?: string | undefined;
  readonly repositoryIdentity?: Project["repositoryIdentity"] | undefined;
  readonly defaultThreadEnvMode?: Project["defaultThreadEnvMode"] | undefined;
  readonly cmmdRegistration?: Project["cmmdRegistration"] | undefined;
  readonly defaultModelSelection?: Project["defaultModelSelection"] | undefined;
  readonly scripts?: ReadonlyArray<Project["scripts"][number]> | undefined;
  readonly updatedAt: string;
}

/**
 * Apply a `project.meta-updated` payload to the project it names.
 *
 * Every field branches on `!== undefined` rather than on truthiness, and that
 * is the whole content of this function. A meta update is a PATCH: absent means
 * "this event says nothing about that field", while `null` is a real value
 * meaning "cleared". Collapsing them either way breaks something visible —
 * treating absent as null wipes a project's model default every time it is
 * renamed, and treating null as absent makes the settings page's reset buttons
 * do nothing at all.
 */
export function applyProjectMetaUpdate(
  project: Project,
  payload: ProjectMetaUpdatePayload,
): Project {
  return {
    ...project,
    ...(payload.title !== undefined ? { name: payload.title } : {}),
    ...(payload.workspaceRoot !== undefined ? { cwd: payload.workspaceRoot } : {}),
    ...(payload.repositoryIdentity !== undefined
      ? { repositoryIdentity: payload.repositoryIdentity ?? null }
      : {}),
    ...(payload.defaultThreadEnvMode !== undefined
      ? { defaultThreadEnvMode: payload.defaultThreadEnvMode }
      : {}),
    ...(payload.cmmdRegistration !== undefined
      ? { cmmdRegistration: payload.cmmdRegistration }
      : {}),
    ...(payload.defaultModelSelection !== undefined
      ? {
          defaultModelSelection: payload.defaultModelSelection
            ? normalizeModelSelection(payload.defaultModelSelection)
            : null,
        }
      : {}),
    ...(payload.scripts !== undefined ? { scripts: mapProjectScripts(payload.scripts) } : {}),
    updatedAt: payload.updatedAt,
  };
}
